/**
 * Hedita Enterprise - Host API Queries (Guest App)
 * Tabella: team_members (ruolo = 'host')
 */

import { supabase } from '../client';
import type { QueryOptions } from '@/types';

const TABLE = 'team_members';

export interface HostProfile {
  id: string;
  admin_id: string;
  nome: string;
  cognome?: string;
  email?: string;
  telefono?: string;
  guest_app_bio?: string;
  guest_app_foto_url?: string;
  is_visible_on_guest_app: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================
// GET Queries
// ============================================================

export async function getHosts(options?: QueryOptions & { adminId?: string }) {
  const adminId = options?.adminId;
  if (!adminId) throw new Error('adminId required');
  
  let query = supabase
    .from(TABLE)
    .select('*', { count: options?.count })
    .eq('admin_id', adminId)
    .eq('ruolo', 'host')
    .eq('attivo', true)
    .order('nome', { ascending: true });

  // Filter by visibility
  if (options?.filters?.is_visible_on_guest_app !== undefined) {
    query = query.eq('is_visible_on_guest_app', options.filters.is_visible_on_guest_app);
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;
  
  return { 
    data: data as HostProfile[] | null, 
    error, 
    count 
  };
}

export async function getVisibleHosts(adminId?: string) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, nome, cognome, guest_app_bio, guest_app_foto_url, telefono, email')
    .eq('admin_id', adminId)
    .eq('ruolo', 'host')
    .eq('is_visible_on_guest_app', true)
    .eq('attivo', true)
    .order('nome', { ascending: true });

  return {
    data: data as HostProfile[] | null,
    error
  };
}

export async function getHost(id: string, adminId?: string) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('admin_id', adminId)
    .eq('ruolo', 'host')
    .single();

  return {
    data: data as HostProfile | null,
    error
  };
}

// ============================================================
// MUTATIONS
// ============================================================

export async function updateHostVisibility(
  id: string, 
  isVisible: boolean, 
  adminId?: string
) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update({ is_visible_on_guest_app: isVisible })
    .eq('id', id)
    .eq('admin_id', adminId)
    .eq('ruolo', 'host')
    .select()
    .single();

  return {
    data: data as HostProfile | null,
    error
  };
}

export async function updateHostProfile(
  id: string, 
  updates: { 
    guest_app_bio?: string; 
    guest_app_foto_url?: string;
    telefono?: string;
    email?: string;
  }, 
  adminId?: string
) {
  if (!adminId) throw new Error('adminId required');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('admin_id', adminId)
    .eq('ruolo', 'host')
    .select()
    .single();

  return {
    data: data as HostProfile | null,
    error
  };
}

// ============================================================
// RETRO-COMPATIBILITÀ (Legacy names)
// ============================================================

/** @deprecated Usa getHosts */
export const getHostProfiles = getHosts;
/** @deprecated Usa getHost */
export const getHostProfileById = getHost;
/** @deprecated Usa getVisibleHosts */
export const createHostProfile = getVisibleHosts;
/** @deprecated Usa updateHostProfile */
export const updateHostProfileById = updateHostProfile;
/** @deprecated Non usare - stub per compatibilità */
export async function deleteHostProfile(id: string, adminId?: string) {
  if (!adminId) throw new Error('adminId required');
  const { error } = await supabase.from('team_members').delete().eq('id', id).eq('admin_id', adminId).eq('ruolo', 'host');
  return { error };
}
/** @deprecated Non usare - stub per compatibilità */
export async function syncHostStruttura(hostId: string, strutturaId: string, adminId?: string) {
  if (!adminId) throw new Error('adminId required');
  return { error: null };
}
